import ContentView16 from '@carbon/icons-react/es/content-view/16';
import DocumentAdd16 from '@carbon/icons-react/es/document--add/16';
import Information16 from '@carbon/icons-react/es/information/16';
import { Switch, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { RateCardLite, RateCardMutationParams } from 'api/rate-cards';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Table } from 'components/molecules/table';
import { toastService } from 'components/molecules/toast';
import { SingleLineEllipsis } from 'components/styles';
import { PermissionsContext } from 'contexts/permissions';
import { useTranslation } from 'hooks/use-translation';
import { useUpdateRateCardMutation } from 'queries/rate-cards';
import { rateCardsSorter } from 'utils/project-financials';

// Keeping commented code here as deletion is not needed now, but will be hanlded later
// const MoreOptionsMenuDivider = styled(Menu.Divider)`
//   margin-left: var(--spacing-05) !important;
//   margin-right: var(--spacing-05) !important;
// `;

const StyledButton = styled(Button)`
  padding-left: 0;
  padding-right: 0;
  width: 100%;
  &:hover {
    background-color: transparent;
  }
`;

const StyledTable = styled(Table)`
  margin: var(--spacing-05) 0;

  .ant-table-cell {
    background-color: transparent;
    padding-top: 0;
    padding-bottom: 0;
    height: var(--layout-04);
  }

  .ant-table-row {
    &:hover {
      > td {
        background-color: var(--gray-10) !important;
      }
    }
  }
`;

const SpanWithEllipsis = styled.span`
  width: 100%;
  max-width: 33vw;
  ${SingleLineEllipsis};
  text-align: start;
`;

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

interface Props {
  rateCards: RateCardLite[];
  openRateCardDrawer: (rateCardId?: number) => void;
  createNewRateCardVersion: (rateCardId: number) => void;
}

export const RateCardsList: FC<Props> = ({
  rateCards,
  openRateCardDrawer,
  createNewRateCardVersion,
}) => {
  const rateCardNameTitle = useTranslation(
    'settings.rate_cards.list.header.rate_card_name',
  );
  const rateCardEnabled = useTranslation(
    'settings.rate_cards.list.header.enabled',
  );
  const rateCardEnabledTooltip = useTranslation(
    'settings.rate_cards.list.header.enabled.tooltip',
  );
  const rateCardEnabledTooltipDisabled = useTranslation(
    'settings.rate_cards.list.header.enabled.tooltip.disabled',
  );
  const updateSuccessToastTitle = useTranslation(
    'settings.rate_cards.rate_card.update.toast.success.title',
  );
  const updateSuccessToastMessage = useTranslation(
    'settings.rate_cards.rate_card.update.toast.success.message',
  );
  const updateErrorToastTitle = useTranslation(
    'settings.rate_cards.rate_card.update.toast.error.title',
  );
  const updateErrorToastMessage = useTranslation(
    'settings.rate_cards.rate_card.update.toast.error.message',
  );
  // const deleteSuccessToastTitle = useTranslation(
  //   'settings.rate_cards.rate_card.delete.toast.success.title',
  // );
  // const deleteSuccessToastMessage = useTranslation(
  //   'settings.rate_cards.rate_card.delete.toast.success.message',
  // );
  // const deleteErrorToastTitle = useTranslation(
  //   'settings.rate_cards.rate_card.delete.toast.error.title',
  // );
  // const deleteErrorToastMessage = useTranslation(
  //   'settings.rate_cards.rate_card.delete.toast.error.message',
  // );

  const { canCreate, canWrite /* canDelete */ } =
    useContext(PermissionsContext);

  const [rateCardUpdateId, setRateCardUpdateId] =
    useState<number | undefined>();
  // const [rateCardDeleteId, setRateCardDeleteId] =
  //   useState<number | undefined>();

  const { mutateAsync: updateRateCard } = useUpdateRateCardMutation();
  // const { mutateAsync: deleteRateCard } = useDeleteRateCardMutation();

  const updateRateCardHandler = useCallback(
    async (params: RateCardMutationParams) => {
      setRateCardUpdateId(params.rateCardId);
      try {
        await updateRateCard(params);
        toastService({
          title: updateSuccessToastTitle,
          message: updateSuccessToastMessage,
        }).success();
      } catch (e) {
        toastService({
          title: updateErrorToastTitle,
          message: updateErrorToastMessage,
        }).error();
      }
      setRateCardUpdateId(undefined);
    },
    [
      updateErrorToastMessage,
      updateErrorToastTitle,
      updateRateCard,
      updateSuccessToastMessage,
      updateSuccessToastTitle,
    ],
  );

  // const deleteRateCardHandler = useCallback(
  //   async (rateCardId: number) => {
  //     setRateCardDeleteId(rateCardId);
  //     try {
  //       await deleteRateCard(rateCardId);
  //       toastService({
  //         title: deleteSuccessToastTitle,
  //         message: deleteSuccessToastMessage,
  //       }).success();
  //     } catch (e) {
  //       toastService({
  //         title: deleteErrorToastTitle,
  //         message: deleteErrorToastMessage,
  //       }).error();
  //     }
  //     setRateCardDeleteId(undefined);
  //   },
  //   [
  //     deleteErrorToastMessage,
  //     deleteErrorToastTitle,
  //     deleteRateCard,
  //     deleteSuccessToastMessage,
  //     deleteSuccessToastTitle,
  //   ],
  // );

  const orderedRateCard = useMemo(
    () => rateCardsSorter(rateCards),
    [rateCards],
  );

  const columns: ColumnsType<RateCardLite> = [
    {
      title: rateCardNameTitle,
      dataIndex: 'rateCardName',
      key: 'rateCardName',
      width: '50%',
      render: (rateCardName, record) => {
        return (
          <StyledButton
            type="text"
            onClick={() => {
              openRateCardDrawer(record.rateCardId);
            }}
          >
            <SpanWithEllipsis>{rateCardName}</SpanWithEllipsis>
          </StyledButton>
        );
      },
    },
    {
      title: (
        <StyledDiv>
          {rateCardEnabled}
          <Tooltip
            title={
              canWrite ? rateCardEnabledTooltip : rateCardEnabledTooltipDisabled
            }
          >
            <Information16 />
          </Tooltip>
        </StyledDiv>
      ),
      dataIndex: 'enabled',
      key: 'enabled',
      width: '50%',
      render: (enabled, rateCard) => {
        return (
          <StyledDiv>
            <Switch
              size="small"
              checked={enabled}
              loading={
                rateCardUpdateId != null &&
                rateCardUpdateId === rateCard.rateCardId
              }
              onChange={(checked) => {
                updateRateCardHandler({
                  enabled: checked,
                  rateCardId: rateCard.rateCardId,
                });
              }}
              disabled={!canWrite}
            />
            {enabled ? (
              <FormattedMessage id="yes" />
            ) : (
              <FormattedMessage id="no" />
            )}
          </StyledDiv>
        );
      },
    },
    {
      title: '',
      dataIndex: 'rateCardId',
      key: 'moreActions',
      width: '48px',
      render: (rateCardId) => {
        /* return rateCardDeleteId != null && rateCardDeleteId === rateCardId ? (
          <Loading />
        ) : */ return (
          <MoreActionsDropdown
            menu={
              <Menu>
                <Menu.Header>
                  <FormattedMessage id="more_actions" />
                </Menu.Header>
                <Menu.Item
                  key="edit"
                  icon={<ContentView16 />}
                  onClick={() => {
                    openRateCardDrawer(rateCardId);
                  }}
                >
                  <FormattedMessage id="settings.rate_cards.list.actions.view" />
                </Menu.Item>
                {canCreate && (
                  <Menu.Item
                    key="edit"
                    icon={<DocumentAdd16 />}
                    onClick={() => {
                      createNewRateCardVersion(rateCardId);
                    }}
                  >
                    <FormattedMessage id="settings.rate_cards.list.actions.create" />
                  </Menu.Item>
                )}

                {/* commenting delete as its not needed as of now */}
                {/* {canDelete && (
                  <>
                    <MoreOptionsMenuDivider />
                    <Menu.ItemDelete
                      key="delete"
                      onClick={() => {
                        deleteRateCardHandler(rateCardId);
                      }}
                    >
                      <FormattedMessage id="delete" />
                    </Menu.ItemDelete>
                  </>
                )} */}
              </Menu>
            }
          />
        );
      },
    },
  ];

  return (
    <StyledTable
      columns={columns}
      dataSource={orderedRateCard}
      pagination={false}
    />
  );
};
